import React from 'react';
import { HStack, Text, IconButton, Icon, Pressable, Box } from 'native-base';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import { getText } from 'src/utils/i18n';
interface DatasetListItemProps {
  item: any;
  rowNumber: number;
  onDatasetPress: (item: any) => void;
  onLabelDataset: (datasetID: string) => void;
  onUploadImages: (datasetID: string) => void;
  onDeleteDataset: ((datasetID: string) => void) | null;
  onViewStatistics: (item: any) => void;
  getGrainName: (grainId: string) => string;
}
const DatasetListItem: React.FC<DatasetListItemProps> = ({
  item,
  rowNumber,
  onDatasetPress,
  onLabelDataset,
  onUploadImages,
  onDeleteDataset,
  onViewStatistics,
  getGrainName,
}) => {
  if (!item) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'success.500';
      case 'IN_PROGRESS':
        return 'warning.500';
      case 'NOT_LABELED':
        return 'coolGray.500';
      default:
        return 'primary.500';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'NOT_LABELED':
        return getText('statusNotLabeled');
      case 'IN_PROGRESS':
        return getText('statusInProgress');
      case 'COMPLETED':
        return getText('statusCompleted');
      default:
        return status;
    }
  };

  const formatUpdatedAt = (timestamp: number) => {
    if (!timestamp) return getText('notAvailable');
    const dateObj = new Date(timestamp * 1000);
    const istString = dateObj.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' });
    const istDate = new Date(istString);
    const dd = String(istDate.getDate()).padStart(2, '0');
    const mm = String(istDate.getMonth() + 1).padStart(2, '0');
    const yyyy = istDate.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };
  return (
    <Pressable onPress={() => onLabelDataset(item.datasetID)} width="100%">
      <Box
        bg="white"
        borderRadius="md"
        px={4}
        py={3}
        mb={1}
        borderWidth={1}
        borderColor="coolGray.200"
        _hover={{ bg: "coolGray.50" }}
        shadow={1}
      >
        <HStack
          space={2}
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          <Text flex={0.5} textAlign="center" fontSize="sm" color="coolGray.600">
            {rowNumber}
          </Text>
          <Text flex={2} fontWeight="medium" fontSize="sm" color="gray.800" isTruncated>
            {item.datasetID}
          </Text>
          <Text flex={1} fontSize="sm" color="gray.700" isTruncated>
            {getGrainName(item.grainId)}
          </Text>
          <Text flex={1} fontSize="sm" color="gray.700" isTruncated>
            {item.username}
          </Text>
          <Box flex={1} alignItems="center">
            <Box
              px={2}
              py={1}
              borderRadius="full"
              bg={getStatusColor(item.status)}
            >
              <Text fontSize="xs" fontWeight="medium" color="white">
                {getStatusText(item.status)}
              </Text>
            </Box>
          </Box>
          <Text flex={1} textAlign="center" fontSize="sm" color="gray.700">
            {formatUpdatedAt(item.updatedAt)}
          </Text>
          <HStack space={1} alignItems="center" minW="160">
            {/* Statistics icon */}
            <IconButton
              size="sm"
              variant="ghost"
              _pressed={{ bg: "coolGray.200" }}
              icon={<Icon as={MaterialCommunityIcons} name="chart-bar" size="sm" color="primary.600" />}
              onPress={(e) => {
                e.stopPropagation();
                onViewStatistics(item);
              }}
              aria-label="View Statistics"
            />

            {/* Label icon */}
            <IconButton
              size="sm"
              variant="ghost"
              _pressed={{ bg: "coolGray.200" }}
              icon={<Icon as={MaterialCommunityIcons} name="label-multiple" size="sm" color="success.600" />}
              onPress={(e) => {
                e.stopPropagation();
                onLabelDataset(item.datasetID);
              }}
              aria-label="Label Dataset"
            />

            {/* Upload icon */}
            <IconButton
              size="sm"
              variant="ghost"
              _pressed={{ bg: "coolGray.200" }}
              icon={<Icon as={MaterialCommunityIcons} name="upload" size="sm" color="info.600" />}
              onPress={(e) => {
                e.stopPropagation();
                onUploadImages(item.datasetID);
              }}
              aria-label="Upload Images"
            />

            {/* Delete icon */}
            {onDeleteDataset && (
              <IconButton
                size="sm"
                variant="ghost"
                _pressed={{ bg: "red.100" }}
                icon={<Icon as={MaterialCommunityIcons} name="delete" size="sm" color="red.500" />}
                onPress={(e) => {
                  e.stopPropagation();
                  onDeleteDataset(item.datasetID);
                }}
                aria-label="Delete Dataset"
              />
            )}
          </HStack>
        </HStack>
      </Box>
    </Pressable>
  );
};

export default DatasetListItem;